import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/site/ThemeProvider";
import { SiteContentProvider } from "@/lib/SiteContentProvider";
import { CatalogProvider } from "@/lib/CatalogProvider";
import { PortfolioProvider } from "@/lib/PortfolioProvider";
import { ReviewsProvider } from "@/lib/ReviewsProvider";
import { HeroRailProvider } from "@/lib/HeroRailProvider";
import Preloader from "@/components/site/Preloader";
import SiteLayout from "@/components/site/SiteLayout";
import ScrollToTop from "@/components/site/ScrollToTop";
import HomePage from "@/pages/Home";
import AboutPage from "@/pages/site/AboutPage";
import ServicesPage from "@/pages/site/ServicesPage";
import WorkPage from "@/pages/site/WorkPage";
import ProjectDetail from "@/pages/site/ProjectDetail";
import GlobalPage from "@/pages/site/GlobalPage";
import ContactPage from "@/pages/site/ContactPage";
import DeliveryPortal from "@/pages/DeliveryPortal";
import StartProject from "@/pages/StartProject";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPortfolio from "@/pages/admin/AdminPortfolio";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminHeroRail from "@/pages/admin/AdminHeroRail";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import GA4RouteTracker from "@/analytics/GA4RouteTracker";

function App() {
    useEffect(() => {
        document.documentElement.lang = "en";

        // Register service worker (PWA) — only in production-like browsers.
        // We proactively call registration.update() on every load so users always
        // pick up the latest SW quickly (guards against stale-cache bugs).
        if ("serviceWorker" in navigator) {
            const onLoad = () => {
                navigator.serviceWorker
                    .register("/service-worker.js", { scope: "/" })
                    .then((reg) => {
                        try { reg.update(); } catch (_e) { /* ignore */ }
                    })
                    .catch(() => { /* silent — PWA is progressive enhancement */ });
            };
            window.addEventListener("load", onLoad, { once: true });
            return () => window.removeEventListener("load", onLoad);
        }
    }, []);

    return (
        <ThemeProvider>
            <SiteContentProvider>
                <CatalogProvider>
                    <PortfolioProvider>
                        <ReviewsProvider>
                            <HeroRailProvider>
                                <div className="App">
                                    <Preloader />
                                    <BrowserRouter>
                                    <GA4RouteTracker />
                                    <ScrollToTop />
                                    <Routes>
                                        {/* Public marketing site — shared chrome (Nav + Footer + sticky CTA) */}
                                        <Route element={<SiteLayout />}>
                                            <Route path="/" element={<HomePage />} />
                                            <Route path="/about" element={<AboutPage />} />
                                            <Route path="/services" element={<ServicesPage />} />
                                            <Route path="/work" element={<WorkPage />} />
                                            <Route path="/work/:slug" element={<ProjectDetail />} />
                                            <Route path="/global" element={<GlobalPage />} />
                                            <Route path="/contact" element={<ContactPage />} />
                                        </Route>

                                        {/* Multi-step inquiry flow (own chrome) */}
                                        <Route path="/start" element={<StartProject />} />
                                        <Route path="/start/package" element={<StartProject mode="package-list" />} />
                                        <Route path="/start/package/:packageId" element={<StartProject mode="package-list" />} />
                                        <Route path="/start/custom" element={<StartProject mode="custom" />} />

                                        {/* Public client delivery portal */}
                                        <Route path="/delivery" element={<DeliveryPortal />} />

                                        {/* Admin CMS */}
                                        <Route path="/admin/login" element={<AdminLogin />} />
                                        <Route path="/admin" element={<AdminLayout />}>
                                            <Route index element={<Navigate to="/admin/inquiries" replace />} />
                                            <Route path="inquiries" element={<AdminInquiries />} />
                                            <Route path="projects" element={<AdminProjects />} />
                                            <Route path="content" element={<AdminContent />} />
                                            <Route path="portfolio" element={<AdminPortfolio />} />
                                            <Route path="reviews" element={<AdminReviews />} />
                                            <Route path="hero-rail" element={<AdminHeroRail />} />
                                            <Route path="packages" element={<AdminPackages />} />
                                            <Route path="coupons" element={<AdminCoupons />} />
                                        </Route>

                                        {/* Fallback */}
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </BrowserRouter>
                                <Sonner richColors position="top-center" />
                                </div>
                            </HeroRailProvider>
                        </ReviewsProvider>
                    </PortfolioProvider>
                </CatalogProvider>
            </SiteContentProvider>
        </ThemeProvider>
    );
}

export default App;
